import React, { useState, useEffect, ReactElement } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from '@Common/Button';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import Moment from 'moment';
import Notification from '@Common/Notification';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import { DATE_FORMAT } from '@Constants/constants';
import { useGetEligibleProjectsForCompression, useCompressTaskDates } from '../../../../../apis/locationTask';
import type { EligibleProject, CompressCommand } from '../../../../../apis/locationTask/locationTask.types';

interface TaskCompressionModalProps {
    isOpen: boolean;
    onClose: () => void;
    locationId: number;
    onCompressionComplete: () => void;
}

interface ProjectsInfo {
    error?: string;
    firstTaskDate?: string;
    zeroDate?: string;
    oldDiff?: number;
    zeroDateMoment?: Moment.Moment;
}

const TaskCompressionModal = ({ isOpen, onClose, locationId, onCompressionComplete }: TaskCompressionModalProps): ReactElement => {
    const [selectedProjects, setSelectedProjects] = useState<EligibleProject[]>([]);
    const [newStartDate, setNewStartDate] = useState<Date | null>(null);
    const [acknowledged, setAcknowledged] = useState<boolean>(false);

    const { data: eligibleProjectsData, isLoading, isError, error } = useGetEligibleProjectsForCompression(
        locationId,
        { enabled: isOpen && !!locationId }
    );

    const { mutate: runCompress, isPending: isCompressing } = useCompressTaskDates((response) => {
        Notification('success', 'Compression Complete',
            `${response.tasksUpdated} task(s) compressed from ${response.oldDurationDays} to ${response.newDurationDays} days.`);
        onCompressionComplete();
        onClose();
    });

    useEffect(() => {
        if (!isOpen) {
            setSelectedProjects([]);
            setNewStartDate(null);
            setAcknowledged(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !eligibleProjectsData) return;
        if (!eligibleProjectsData.isFeatureEnabled) {
            Notification('error', 'Feature Not Available', 'The Task Compression Utility is not enabled for your role.');
            onClose();
        }
    }, [eligibleProjectsData, isOpen]);

    useEffect(() => {
        if (!isOpen || !isError) return;
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred.';
        Notification('error', 'Error', errorMessage);
        onClose();
    }, [isError, isOpen]);

    const eligibleProjects = eligibleProjectsData?.projects ?? [];

    const getProjectsInfo = (): ProjectsInfo | null => {
        if (!selectedProjects || selectedProjects.length === 0) return null;

        const zeroDates = [...new Set(
            selectedProjects.map(p => Moment.utc(p.zeroDate).format(DATE_FORMAT))
        )];
        if (zeroDates.length > 1) {
            return { error: 'Selected projects must have the same Zero Date to be compressed together.' };
        }

        const zeroDateMoment = Moment.utc(selectedProjects[0].zeroDate);

        const oldDiff = Math.max(...selectedProjects.map(p => p.currentDurationDays));

        const earliestTaskDate = Moment.utc(selectedProjects[0].zeroDate)
            .clone()
            .subtract(oldDiff, 'days');

        return {
            firstTaskDate: earliestTaskDate.format(DATE_FORMAT),
            zeroDate: zeroDateMoment.format(DATE_FORMAT),
            oldDiff,
            zeroDateMoment,
        };
    };

    const getNewDiff = (info: ProjectsInfo | null): number | null => {
        if (!info || info.error || !newStartDate) return null;
        const newStart = Moment(newStartDate).utc(true).startOf('day');
        const zero = info.zeroDateMoment!.clone().startOf('day');
        const newDiff = zero.diff(newStart, 'days');
        if (newDiff <= 0 || newDiff >= info.oldDiff!) return null;
        return newDiff;
    };

    const projectsInfo = getProjectsInfo();
    const newDiff = getNewDiff(projectsInfo);
    const isNewStartDateInvalid = newStartDate && projectsInfo && !projectsInfo.error && !newDiff;
    const canCompress = !!(projectsInfo && !projectsInfo.error && newDiff && acknowledged);

    const onCompress = () => {
        if (!canCompress) return;

        const command: CompressCommand = {
            locationId,
            projectLocationIds: selectedProjects.map(p => p.projectLocationId),
            newStartDate: Moment(newStartDate).utc(true).format('YYYY-MM-DD'),
        };

        runCompress(command);
    };

    return (
        <Modal show={isOpen} onHide={onClose} size='lg' centered dialogClassName='modal-61-dialog'>
            <LoadingIndicator isLoading={isLoading || isCompressing}>
                <Modal.Header closeButton>
                    <Modal.Title>Task Compression Utility</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='row'>
                        <div className='col-sm-6'>
                            <div className='mb-1'>
                                <label>Project(s):</label>
                            </div>
                            <MultiSelect
                                value={selectedProjects}
                                options={eligibleProjects}
                                onChange={(e) => setSelectedProjects(e.value)}
                                optionLabel='projectName'
                                dataKey='projectLocationId'
                                placeholder='Select projects...'
                                className='w-100 pacer-multiselect'
                                display='chip'
                            />
                            <small className='text-muted d-block mt-1'>
                                Projects must have the same zero date to be compressed together.
                            </small>
                            {projectsInfo && projectsInfo.error && (
                                <div className='text-danger mt-1 small'>{projectsInfo.error}</div>
                            )}
                        </div>
                        {projectsInfo && !projectsInfo.error && (
                            <>
                                <div className='col-sm-6 mt-3'>
                                    <div className='p-3 bg-light rounded'>
                                        Based on the projects selected, the first task starts on{' '}
                                        <strong>{projectsInfo.firstTaskDate}</strong>.
                                        {' '}The Zero Date is <strong>{projectsInfo.zeroDate}</strong>.
                                        {' '}That is <strong>{projectsInfo.oldDiff}</strong> days.
                                    </div>
                                </div>

                            </>
                        )}
                    </div>
                    <div className='row mt-3'>
                        <div className='col-sm-6'>
                            <div className='mb-1'>
                                <label>Start Date:</label>
                            </div>
                            <Calendar
                                value={newStartDate}
                                onChange={(e) => setNewStartDate(e.value as Date | null)}
                                dateFormat='dd M yy'
                                showIcon
                                iconPos='left'
                                placeholder='When'
                                className='pacer-calendar'
                            />
                            {isNewStartDateInvalid && (
                                <div className='text-danger mt-1 small'>
                                    The start date must be before the Zero Date and result in fewer days than the current schedule span.
                                </div>
                            )}
                        </div>
                        {projectsInfo && !projectsInfo.error && (
                            <>
                                {newDiff && (
                                    <div className='col-sm-6 mt-3'>
                                        <div className='p-3 bg-light rounded'>
                                            The tasks will be compressed from{' '}
                                            <strong>{projectsInfo.oldDiff}</strong> days to{' '}
                                            <strong>{newDiff}</strong> days using an early curve equation.
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between align-items-center'>
                    <div className='d-flex align-items-start gap-2' style={{ maxWidth: '75%' }}>
                        <input
                            type='checkbox'
                            id='compressionAcknowledge'
                            className='form-check-input mt-1 flex-shrink-0'
                            checked={acknowledged}
                            onChange={(e) => setAcknowledged(e.target.checked)}
                        />
                        <label htmlFor='compressionAcknowledge' className='form-check-label small text-muted'>
                            I understand there are risks to compressing the tasks. Please read the help article for risk information.
                            Tasks that are completed will not be changed. Tasks that are due at T-0 or after will not be changed.
                        </label>
                    </div>
                    <Button type='button' text='Compress' bootstrapClass='primary' onClick={onCompress} isDisabled={!canCompress || isCompressing} />
                </Modal.Footer>
            </LoadingIndicator>
        </Modal>
    );
};

export default TaskCompressionModal;
